import { Box, Button, Chip, Tab, Tabs, Typography } from '@mui/material'
import type { SyntheticEvent } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import CreateAggregateBidsButton from '@/features/bidding/components/cascading/review/CreateAggregateBidsButton'
import type { VolumeBidLevelColumn } from '@/features/bidding/components/cascading/review/ReviewBidsTableVolume'
import ReviewBidsTableVolume from '@/features/bidding/components/cascading/review/ReviewBidsTableVolume'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import {
  buildAggregateHourlyMap,
  resolveVolumeCapacityForHour,
} from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'
import {
  buildAsymmetricalBidOfferRequest,
  buildSymmetricalBidOfferRequest,
} from '@/features/bidding/utils/cascading/buildAggregateBidOfferRequest'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

type ReviewBidsContentVolumeBaseProps = {
  deliveryDay: MarketDate
  selectedMarket: CascadeMarketId
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null
  aggregateAvailability: AggregateAvailabilityResponse | null
  onBackClick: () => void
  onSuccess: () => void
}

type ReviewBidsContentVolumeProps = Readonly<
  ReviewBidsContentVolumeBaseProps &
    ({ isSymmetrical: false; levels: BidLevelInput[] } | { isSymmetrical: true; levels: BidLevelInputSymmetrical[] })
>

function buildColumnsForAsymmetrical(
  levels: BidLevelInput[],
  direction: 'up' | 'down',
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW: number,
): VolumeBidLevelColumn[] {
  const columns: VolumeBidLevelColumn[] = []

  for (const level of levels) {
    const fixedPrice = direction === 'up' ? level.upPrice : level.downPrice

    // Skip levels without a price set for this direction
    if (fixedPrice == null) continue

    const volumeConfig = direction === 'up' ? level.upVolumeCapacity : level.downVolumeCapacity

    const hourlyVolumeMW: number[] = []
    for (let hour = 0; hour < 24; hour++) {
      hourlyVolumeMW.push(resolveVolumeCapacityForHour(volumeConfig, hour, suggestedMap, availableMap, granularityMW))
    }

    columns.push({
      levelId: level.id,
      priceLabel: fixedPrice.toFixed(2),
      hourlyVolumeMW,
    })
  }

  return columns
}

function buildColumnsForSymmetrical(
  levels: BidLevelInputSymmetrical[],
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW: number,
): VolumeBidLevelColumn[] {
  const columns: VolumeBidLevelColumn[] = []

  for (const level of levels) {
    const fixedPrice = level.price

    // Skip levels without a price set
    if (fixedPrice == null) continue

    const volumeConfig = level.volumeCapacity

    const hourlyVolumeMW: number[] = []
    for (let hour = 0; hour < 24; hour++) {
      hourlyVolumeMW.push(resolveVolumeCapacityForHour(volumeConfig, hour, suggestedMap, availableMap, granularityMW))
    }

    columns.push({
      levelId: level.id,
      priceLabel: fixedPrice.toFixed(2),
      hourlyVolumeMW,
    })
  }

  return columns
}

function ReviewBidsContentVolume(props: ReviewBidsContentVolumeProps) {
  const { deliveryDay, selectedMarket, aggregateSuggestedBids, aggregateAvailability, onBackClick, onSuccess } = props

  const { t } = useTranslation()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const [activeTab, setActiveTab] = useState(0)

  const marketLabel = CASCADE_MARKETS.find((m) => m.id === selectedMarket)?.label ?? selectedMarket
  const { stepMW: granularityMW, decimalPlaces: volumeDecimalPlaces } = getMarketVolumeGranularity(selectedMarket)

  // Build aggregate hourly maps for resolving volume capacity
  const {
    suggestedUpMap,
    suggestedDownMap,
    suggestedSymmetricMap,
    availableUpMap,
    availableDownMap,
    availableSymmetricMap,
  } = useMemo(
    () => ({
      suggestedUpMap: buildAggregateHourlyMap(aggregateSuggestedBids, 'up'),
      suggestedDownMap: buildAggregateHourlyMap(aggregateSuggestedBids, 'down'),
      suggestedSymmetricMap: buildAggregateHourlyMap(aggregateSuggestedBids, 'symmetric'),
      availableUpMap: buildAggregateHourlyMap(aggregateAvailability, 'up'),
      availableDownMap: buildAggregateHourlyMap(aggregateAvailability, 'down'),
      availableSymmetricMap: buildAggregateHourlyMap(aggregateAvailability, 'symmetric'),
    }),
    [aggregateSuggestedBids, aggregateAvailability],
  )

  // Build columns based on market type
  const { upColumns, downColumns, allColumns } = useMemo(() => {
    if (!props.isSymmetrical) {
      const up = buildColumnsForAsymmetrical(props.levels, 'up', suggestedUpMap, availableUpMap, granularityMW)
      const down = buildColumnsForAsymmetrical(props.levels, 'down', suggestedDownMap, availableDownMap, granularityMW)
      return { upColumns: up, downColumns: down, allColumns: [] }
    }

    const all = buildColumnsForSymmetrical(props.levels, suggestedSymmetricMap, availableSymmetricMap, granularityMW)
    return { upColumns: [], downColumns: [], allColumns: all }
  }, [
    props.isSymmetrical,
    props.levels,
    suggestedUpMap,
    suggestedDownMap,
    suggestedSymmetricMap,
    availableUpMap,
    availableDownMap,
    availableSymmetricMap,
    granularityMW,
  ])

  // Build the aggregate bid offer request
  const bidOfferRequest = useMemo(() => {
    if (!props.isSymmetrical) {
      return buildAsymmetricalBidOfferRequest(
        props.levels,
        deliveryDay,
        suggestedUpMap,
        suggestedDownMap,
        availableUpMap,
        availableDownMap,
        granularityMW,
      )
    }

    return buildSymmetricalBidOfferRequest(
      props.levels,
      deliveryDay,
      suggestedSymmetricMap,
      availableSymmetricMap,
      granularityMW,
    )
  }, [
    props.isSymmetrical,
    props.levels,
    deliveryDay,
    suggestedUpMap,
    suggestedDownMap,
    suggestedSymmetricMap,
    availableUpMap,
    availableDownMap,
    availableSymmetricMap,
    granularityMW,
  ])

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header with title, market chip, and delivery day */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('bidding.bidding_cascading.review_bids.title')}
        </Typography>
        <Chip
          label={marketLabel}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          }}
        />
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          •
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {deliveryDay.toString()}
        </Typography>
      </Box>

      {/* FCR-D: UP/DOWN tabs */}
      {!props.isSymmetrical && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            aria-label={t('bidding.bidding_cascading.review_bids.market_direction_tabs')}
            value={activeTab}
            onChange={handleTabChange}
          >
            <Tab
              disabled={upColumns.length === 0}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{t('bidding.bidding_cascading.review_bids.up_tab', { count: upColumns.length })}</span>
                  <Chip
                    label={upColumns.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: upColumns.length > 0 ? 'success.lighter' : 'action.disabledBackground',
                      color: upColumns.length > 0 ? 'success.dark' : 'text.disabled',
                    }}
                  />
                </Box>
              }
            />
            <Tab
              disabled={downColumns.length === 0}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{t('bidding.bidding_cascading.review_bids.down_tab', { count: downColumns.length })}</span>
                  <Chip
                    label={downColumns.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: downColumns.length > 0 ? 'error.lighter' : 'action.disabledBackground',
                      color: downColumns.length > 0 ? 'error.dark' : 'text.disabled',
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>
      )}

      {/* Bids table */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 0 }}>
        {props.isSymmetrical ? (
          <ReviewBidsTableVolume columns={allColumns} volumeDecimalPlaces={volumeDecimalPlaces} />
        ) : (
          <ReviewBidsTableVolume
            columns={activeTab === 0 ? upColumns : downColumns}
            volumeDecimalPlaces={volumeDecimalPlaces}
          />
        )}
      </Box>

      {/* Bottom Actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button variant="outlined" onClick={onBackClick}>
          {t('bidding.bidding_cascading.review_bids.back_to_bid_ladder')}
        </Button>
        {canMutateBidding && (
          <CreateAggregateBidsButton
            bidCount={bidOfferRequest.bids.length}
            request={bidOfferRequest}
            onSuccess={onSuccess}
          />
        )}
      </Box>
    </Box>
  )
}

export default ReviewBidsContentVolume
